import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Input } from '../ui/Input';
import { PasswordInput } from '../ui/PasswordInput';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { Alert, AlertDescription } from '../ui/Alert';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { extractErrorMessage } from '../../lib/utils/errorHandling';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// AI-themed taglines that rotate
const AI_TAGLINES = [
  "AI-powered candidate matching",
  "Intelligent resume analysis",
  "Smart recruiting insights",
  "Next-gen talent acquisition",
  "Automated candidate screening"
];

// Minimal neural network node component
const NeuralNode: React.FC<{ delay: number; size: 'small' | 'medium' }> = ({ delay, size }) => {
  const sizeClasses = {
    small: 'w-1.5 h-1.5',
    medium: 'w-2.5 h-2.5'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} bg-primary-400 rounded-full absolute opacity-15 animate-neural-pulse`}
      style={{
        animationDelay: `${delay}s`,
        animationDuration: '6s',
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        filter: 'blur(1px)'
      }}
    />
  );
};

// AI data stream component
const DataStream: React.FC<{ delay: number; length: number }> = ({ delay, length }) => {
  return (
    <div
      className="absolute h-px bg-gradient-to-r from-transparent via-primary-300 to-transparent opacity-10 animate-data-flow"
      style={{
        animationDelay: `${delay}s`,
        animationDuration: '15s',
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${length}px`,
        transform: `rotate(${Math.random() * 360}deg)`,
      }}
    />
  );
};


// Animated logo component
const AnimatedLogo: React.FC = () => {
  const [logoLoaded, setLogoLoaded] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setLogoLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative flex items-center justify-center mb-6">
      {/* Otomeyt Logo */}
      <div className={`transition-all duration-1000 ${logoLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
        <img 
          src="https://otomeytfiles.s3.ap-south-1.amazonaws.com/OtomeytLogo/OtomeytLogo_WhiteVer.png"
          alt="Otomeyt AI"
          className="h-8 w-auto object-contain filter brightness-0 invert opacity-80"
          onLoad={() => setLogoLoaded(true)}
        />
      </div>
      
      {/* AI Processing Indicator */}
      <div className={`absolute -right-2 -top-2 w-3 h-3 bg-primary-400 rounded-full ${logoLoaded ? 'animate-ping' : 'opacity-0'}`} />
    </div>
  );
};

export const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTagline, setCurrentTagline] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Watch the email field to get current value
  const emailValue = watch('email');

  // Rotate taglines
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % AI_TAGLINES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);


  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      setIsLoading(true);
      await login(data.email, data.password);
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      // If backend returns PASSWORD_NOT_SET, guide user to OTP
      const detail = (err as any)?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'PASSWORD_NOT_SET') {
        setError('Your account does not have a password yet. Please use OTP login, then set your password.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Enterprise Professional Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background-200 via-background-100 to-background-200">
        
        {/* Minimal Neural Network Nodes - Very subtle */}
        {Array.from({ length: 8 }).map((_, i) => (
          <NeuralNode 
            key={`node-${i}`} 
            delay={i * 1.5} 
            size={i % 3 === 0 ? 'medium' : 'small'} 
          />
        ))}
        
        {/* Minimal Data Streams - Barely visible */}
        {Array.from({ length: 3 }).map((_, i) => (
          <DataStream
            key={`stream-${i}`}
            delay={i * 3}
            length={Math.random() * 100 + 50}
          />
        ))}
        
        {/* Subtle ambient glow - Very low opacity */}
        <div className="absolute inset-0 bg-gradient-radial from-primary-500/2 via-transparent to-transparent opacity-20" />
        
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="glass-card animate-fade-in">
            <CardHeader className="text-center space-y-6 pb-8">
              {/* Otomeyt Branding */}
              <div className="flex flex-col items-center space-y-4">
                <AnimatedLogo />
              </div>

              {/* Main Branding */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <h1 className="text-4xl font-bold text-text-primary">
                    Welcome to{' '}
                    <span 
                      className="text-5xl gradient-text animate-gradient-x"
                      style={{fontFamily: 'Monotype Corsiva, cursive', fontWeight: 'normal'}}
                    >
                      Techmate
                    </span>
                  </h1>
                  <p className="text-text-secondary text-lg font-medium">
                    Your AI recruiting assistant
                  </p>
                </div>

                {/* Subtle rotating taglines */}
                <div className="h-6 flex items-center justify-center">
                  <p 
                    key={currentTagline}
                    className="text-text-secondary text-sm animate-fade-in-out"
                  >
                    {AI_TAGLINES[currentTagline]}
                  </p>
                </div>
              </div>

            </CardHeader>
            
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-body-sm font-medium text-text-primary">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    {...register('email')}
                    className={errors.email ? 'border-error-500 focus:border-error-500 focus:shadow-glow-error' : ''}
                  />
                  {errors.email && (
                    <p className="text-body-sm text-error-500 animate-slide-down">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="text-body-sm font-medium text-text-primary">
                      Password
                    </label>
                    <Link
                      to={emailValue ? `/password/forgot?email=${encodeURIComponent(emailValue)}` : '/password/forgot'}
                      className="text-body-sm text-primary-500 hover:text-primary-400 transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput
                    id="password"
                    placeholder="Enter your password"
                    {...register('password')}
                    error={errors.password?.message}
                    className={errors.password ? 'border-error-500 focus:border-error-500 focus:shadow-glow-error' : ''}
                  />
                </div>

                {error && (
                  <Alert variant="destructive" className="animate-slide-down">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* TODO: Add OTP Login button here to switch flow */}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 text-body-md font-semibold"
                  variant="gradient"
                  size="lg"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Signing In...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>🎯</span>
                      <span>Find Perfect Candidates</span>
                    </div>
                  )}
                </Button>
              </form>

              <div className="text-center">
                <p className="text-body-sm text-text-secondary">
                  Don't have an account?{' '}
                  <a 
                    href="#" 
                    className="text-primary-500 hover:text-primary-400 transition-colors"
                  >
                    Contact your administrator
                  </a>
                </p>
              </div>

            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
};

export default LoginForm;
